"use client";

/**
 * ContentBlockPreview
 * Static, sanitized rendering of a content block's HTML — used as the builder's
 * not-currently-focused preview (so a long form doesn't mount a live TipTap editor per
 * block) and as the public form's read-only rendering of the block. Lives in `shared/`
 * (not `builder/`) because the public renderer depends on it too.
 */

import DOMPurify from "dompurify";
import { memo, useMemo } from "react";
import { RICH_TEXT_ALLOWED_ATTR, RICH_TEXT_ALLOWED_TAGS } from "@/features/forms/_logic/rich-text-sanitize-config";
import { ensureSanitizeHook } from "@/features/forms/_components/renderer/FormDescriptionContent";

interface ContentBlockPreviewProps {
  /** The content block's HTML (already sanitized server-side at save time). */
  html: string;
  /**
   * Shown in place of empty content. Omit this to render nothing when empty — the correct
   * behavior for the public form and the builder's own preview pane, where an empty block
   * must not show internal authoring copy to a viewer. Only the builder's click-to-edit
   * affordance (ContentBlockEditor) should pass an explicit placeholder.
   */
  placeholder?: string;
  className?: string;
}

/**
 * Renders a content block's HTML after DOMPurify sanitization (defense in depth — the source
 * of truth is the server-side sanitizer run at save time, see rich-text-sanitize.ts).
 * @param html - The content block's stored HTML.
 * @param placeholder - Text shown when `html` is empty; omit to render nothing.
 * @param className - Optional wrapper classes.
 * @returns The sanitized content, the placeholder, or null when empty and no placeholder given.
 */
export const ContentBlockPreview = memo(({ html, placeholder, className }: ContentBlockPreviewProps) => {
  const sanitized = useMemo(() => {
    ensureSanitizeHook();
    return DOMPurify.sanitize(html || "", {
      ALLOWED_TAGS: [...RICH_TEXT_ALLOWED_TAGS],
      ALLOWED_ATTR: [...RICH_TEXT_ALLOWED_ATTR],
    });
  }, [html]);

  if (!html?.trim()) {
    if (!placeholder) return null;
    return <p className={`italic text-gray-500 ${className ?? ""}`}>{placeholder}</p>;
  }

  return (
    <div
      className={`prose prose-sm max-w-none [&_a]:text-blue-600 [&_a]:underline ${className ?? ""}`}
      dangerouslySetInnerHTML={{ __html: sanitized }}
    />
  );
});
ContentBlockPreview.displayName = "ContentBlockPreview";
